import React, { useEffect } from 'react'
import Icon from 'react-native-vector-icons/FontAwesome6'
import {
  Easing,
  interpolateColor,
  SharedValue,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { useTheme } from 'styled-components/native'

import Typography from 'APP/Converse/Typography'

import {
  ScaleWrapper,
  Marker,
  LevelContainer,
  LevelBlockContainer,
  LevelBlock,
  LevelLabel,
} from './styles'

export type Level = {
  label?: string
  value: string
}

export type ScaleProps = {
  levels: Level[]
  value: string
  testID?: string
  animated?: boolean
  validateValue?: boolean
}

const MARKER_TRANSITION_MS = 800
const MARKER_TRANSITION_EASING = Easing.inOut(Easing.cubic)

type AnimatedLevelBlockProps = {
  index: number
  levelsCount: number
  isFirstLevel: boolean
  isLastLevel: boolean
  markerLeft: SharedValue<number>
}

const AnimatedLevelBlock: React.FC<AnimatedLevelBlockProps> = ({
  index,
  levelsCount,
  isFirstLevel,
  isLastLevel,
  markerLeft,
}) => {
  const theme = useTheme()
  const segmentWidth = 100 / levelsCount
  const blockCenter = (index + 0.5) * segmentWidth

  const animatedStyle = useAnimatedStyle(() => {
    // How far the marker is from this block's centre, in percentage points.
    const distance = Math.abs(markerLeft.value - blockCenter)
    // 1 when the marker is over this block, 0 once it's a full segment away,
    // linear in between — drives the selected/unselected colour crossfade.
    const proximity = Math.max(0, 1 - distance / segmentWidth)
    return {
      backgroundColor: interpolateColor(
        proximity,
        [0, 1],
        [theme.colors.scaleTrackBg, theme.colors.text]
      ),
    }
  })

  return <LevelBlock style={animatedStyle} isFirstLevel={isFirstLevel} isLastLevel={isLastLevel} />
}

const Scale: React.FC<ScaleProps> = ({
  levels,
  value,
  testID = 'SCALE',
  animated = false,
  validateValue = true,
}) => {
  const theme = useTheme()
  const selectedLevelIndex = levels.findIndex((level) => value === level.value)
  const hasSelection = selectedLevelIndex >= 0
  const iconSize = theme.metrics.icon.small

  const selectedLevelCenterPercent =
    levels.length > 0 ? ((selectedLevelIndex + 0.5) * 100) / levels.length : 0
  const firstLevelCenterPercent = levels.length > 0 ? 50 / levels.length : 0

  const markerLeft = useSharedValue(
    animated && hasSelection ? firstLevelCenterPercent : selectedLevelCenterPercent
  )

  useEffect(() => {
    markerLeft.value = animated
      ? withTiming(selectedLevelCenterPercent, {
          duration: MARKER_TRANSITION_MS,
          easing: MARKER_TRANSITION_EASING,
        })
      : selectedLevelCenterPercent
  }, [animated, markerLeft, selectedLevelCenterPercent])

  const markerAnimatedStyle = useAnimatedStyle(() => ({
    left: `${markerLeft.value}%`,
  }))

  if (!levels.length || (validateValue && !hasSelection)) return null

  return (
    <ScaleWrapper
      testID={testID}
      accessibilityLabel={
        hasSelection
          ? `Scale with levels, level ${selectedLevelIndex + 1} of ${levels.length}, ${
              levels[selectedLevelIndex].label ?? value
            }`
          : `Scale with ${levels.length} levels`
      }
      accessibilityRole="text"
      markerSlotHeight={iconSize + theme.metrics.baseMargin / 4}
    >
      {hasSelection && (
        <Marker
          testID={`${testID}_MARKER`}
          style={markerAnimatedStyle}
          aria-hidden={true}
          iconSize={iconSize}
        >
          <Icon name="location-dot" size={iconSize} color={theme.colors.text} />
        </Marker>
      )}
      <LevelContainer>
        {levels.map((level, index) => (
          <LevelBlockContainer key={level.value}>
            <AnimatedLevelBlock
              index={index}
              levelsCount={levels.length}
              isFirstLevel={index === 0}
              isLastLevel={index === levels.length - 1}
              markerLeft={markerLeft}
            />
            {level?.label && (
              <LevelLabel aria-hidden={true}>
                <Typography color="dim" variant="caption">
                  {level.label}
                </Typography>
              </LevelLabel>
            )}
          </LevelBlockContainer>
        ))}
      </LevelContainer>
    </ScaleWrapper>
  )
}

export default Scale
